import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../lib/auth';
import { can, canEditOwnDraft, canViewRecordAudit } from '../../lib/permissions';
import { api, ApiError } from '../../lib/api';
import { useDetail } from '../../lib/useDetail';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { KeyValue, LineTable, Pill, Spinner } from '../../components/ui';
import { dateLabel, deliveryStatus, num } from '../../lib/format';
import { DELIVERY_ACTIONS, deliveryEditable, type DeliveryAction } from '../../lib/sales';
import { openAuthenticatedPrint } from '../../lib/print';
import type { Delivery, DeliveryLine, RecordAudit } from '../../lib/types';

function serials(raw?: string | null): string[] {
  if (!raw) return [];
  try { const arr = JSON.parse(raw); return Array.isArray(arr) ? arr.map(String) : []; } catch { return []; }
}

export function DeliveryDetail({ row, onEdit, onChanged }: {
  row: Delivery;
  onEdit: (d: Delivery) => void;
  onChanged: () => void;
}) {
  const { user } = useAuth();
  const { data, loading, error, reload } = useDetail<Delivery>(`/api/deliveries/${row.id}`);
  const rec = data ?? row;
  const st = deliveryStatus(rec.status);
  const canWrite = can(user, 'deliveries', 'create');
  const canPrint = can(user, 'deliveries', 'print');
  const canAudit = canViewRecordAudit(user);
  const isLegacy = !!rec.legacy_erpnext_id;
  const canEditDraft = !isLegacy && deliveryEditable(rec.status) && canEditOwnDraft(user, 'deliveries', rec);

  const [busy, setBusy] = useState(false);
  const [actionErr, setActionErr] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<DeliveryAction | null>(null);
  const [auditOpen, setAuditOpen] = useState(false);
  const [printing, setPrinting] = useState(false);
  const audit = useDetail<RecordAudit>(auditOpen ? `/api/audit/deliveries/${row.id}` : null);

  const actions = isLegacy ? [] : (DELIVERY_ACTIONS[rec.status] ?? []);
  const lines = rec.lines ?? [];
  const totalQty = lines.reduce((s, l) => s + Number(l.quantity), 0);

  const doTransition = async (action: DeliveryAction, reason: string) => {
    setBusy(true); setActionErr(null); setNotice(null);
    try {
      await api.post(`/api/deliveries/${rec.id}/status`, { status: action.to, reason: reason || null });
      setNotice(`已变更为「${deliveryStatus(action.to).label}」${action.to === 'confirmed' ? '，已出库并回写合同已交数量' : ''}，状态与操作人已写入审计。`);
      setConfirm(null);
      reload(); onChanged();
    } catch (e) {
      setActionErr(e instanceof ApiError ? e.message : '状态变更失败');
      setConfirm(null);
    } finally { setBusy(false); }
  };

  const doPrint = async () => {
    setPrinting(true); setActionErr(null);
    const r = await openAuthenticatedPrint('delivery', rec.id);
    if (!r.ok) setActionErr(r.message || '打印失败');
    setPrinting(false);
  };

  return (
    <div className="detail" onClick={(e) => e.stopPropagation()}>
      <div className="detail-head">
        <span className="id">{rec.number}</span>
        <h3>{rec.customer_name || '客户'}</h3>
        <Pill tone={st.tone} label={st.label} />
        {isLegacy ? <span className="pill neutral">ERPNext 历史</span> : null}
        <div className="spacer" />
        {loading ? <Spinner label="加载明细…" /> : null}
      </div>
      {error ? <div className="notice bad">{error}</div> : null}
      {notice ? <div className="notice info">{notice}</div> : null}
      {actionErr ? <div className="notice bad" style={{ marginTop: 10 }}>{actionErr}</div> : null}
      {isLegacy ? (
        <div className="notice" style={{ background: 'var(--panel-inset)' }}>
          历史 ERPNext 交货单为只读：不可编辑、不可流转状态，但仍可西语打印 / 导出 REMITO。
        </div>
      ) : null}

      <div className="kv-grid">
        <KeyValue k="来源合同">
          {rec.contract_number
            ? <Link to={`/contracts?q=${encodeURIComponent(rec.contract_number)}`}>{rec.contract_number}</Link>
            : '—'}
        </KeyValue>
        <KeyValue k="客户编码">{rec.customer_code || '—'}</KeyValue>
        <KeyValue k="客户税号">{rec.customer_tax_id || <span className="muted">资料待补</span>}</KeyValue>
        <KeyValue k="收货地址">
          {rec.customer_address
            ? <span>{rec.customer_address}{[rec.customer_city, rec.customer_province, rec.customer_postal_code].filter(Boolean).length ? ` — ${[rec.customer_city, rec.customer_province, rec.customer_postal_code].filter(Boolean).join(' · ')}` : ''}</span>
            : <span className="muted">资料待补</span>}
        </KeyValue>
        <KeyValue k="收货方 IVA 身份">{rec.customer_tax_category || <span className="muted">资料待补</span>}</KeyValue>
        <KeyValue k="交货日期">{dateLabel(rec.delivery_date)}</KeyValue>
        <KeyValue k="客户 PO">{rec.po_no || <span className="muted">未填写（不打印）</span>}</KeyValue>
        <KeyValue k="签发日期">{rec.issue_date ? dateLabel(rec.issue_date) : <span className="muted">默认取交货日期</span>}</KeyValue>
        <KeyValue k="销售点 (P.V.)">{rec.point_of_sale || <span className="muted">未指定</span>}</KeyValue>
        <KeyValue k="Remito 连续编号">{rec.remito_consecutive || <span className="muted">可选</span>}</KeyValue>
        <KeyValue k="出库仓库">{rec.warehouse_id ? '已指定' : '待确认前指定'}</KeyValue>
        <KeyValue k="承运人">{rec.carrier || '—'}</KeyValue>
        <KeyValue k="运单号">{rec.tracking_number || '—'}</KeyValue>
        <KeyValue k="本单总数量"><span className="num">{num(totalQty, 3)}</span></KeyValue>
      </div>

      {rec.notes_es ? <div className="notice" style={{ background: 'var(--panel-inset)' }}>西语备注：{rec.notes_es}</div> : null}

      <LineTable<DeliveryLine>
        rows={lines}
        columns={[
          { header: '编码', render: (l) => <span className="num muted">{l.item_code || '—'}</span> },
          { header: '描述', render: (l) => l.description_es },
          { header: '数量', align: 'right', render: (l) => <span className="num strong">{num(l.quantity, 3)}</span> },
          { header: '单位', render: (l) => <span className="muted">{l.uom}</span> },
          { header: '批次', render: (l) => l.batch_number || <span className="muted">—</span> },
          { header: '序列号', render: (l) => { const s = serials(l.serial_numbers); return s.length ? <span className="num">{s.length} 个 · {s.slice(0, 3).join('、')}{s.length > 3 ? '…' : ''}</span> : <span className="muted">—</span>; } },
        ]}
      />

      {!isLegacy ? (
        <div className="notice info" style={{ marginTop: 12, fontSize: 12.5 }}>
          交货单为内部 <b>REMITO X</b>（NOTA DE RECEPCION / Portal 版式）：客户送修后自提，无需 AFIP/ARCA 税务授权（CAI/CAE 不适用）。
          打印恒含「DOCUMENTO NO VALIDO COMO FACTURA · Código 091」。
        </div>
      ) : null}

      <div className="detail-actions" style={{ flexWrap: 'wrap', marginTop: 4 }}>
        {canWrite && canEditDraft ? (
          <button className="btn primary" disabled={busy} onClick={() => onEdit(rec)}>编辑草稿</button>
        ) : null}
        {actions.map((a) => (
          <button
            key={a.to}
            className={`btn ${a.primary ? 'primary' : ''}`}
            disabled={busy}
            title={a.adminOnly ? '该流转需管理员权限（点击后由服务端校验）' : undefined}
            onClick={() => { setActionErr(null); if (a.confirm) setConfirm(a); else void doTransition(a, ''); }}
          >
            {a.label}{a.adminOnly && user?.role !== 'admin' ? '（需管理员）' : ''}
          </button>
        ))}
        <div className="spacer" />
        {canPrint ? <button className="btn" disabled={printing} onClick={() => void doPrint()} title="打印西语 Nota de Recepción（QC 门户版式，PDF）">{printing ? '准备打印…' : '西语 REMITO / PDF'}</button> : null}
        {canAudit ? <button className="btn link" onClick={() => setAuditOpen((v) => !v)}>{auditOpen ? '收起审计' : '审计记录'}</button> : null}
      </div>
      {!canWrite && !isLegacy ? (
        <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>当前角色只读。交货单的创建、编辑与状态流转由销售 / 仓储 / 管理员执行（服务端强制校验）；西语打印 / 导出 REMITO 仅管理员 / 销售 / 仓库可用。</div>
      ) : null}

      {auditOpen ? (
        <div className="panel" style={{ boxShadow: 'none' }}>
          <div className="panel-body">
            {audit.loading ? <Spinner label="读取审计留痕…" /> : null}
            {audit.error ? <div className="notice bad">{audit.error}</div> : null}
            {audit.data ? (
              <>
                <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>状态流转</div>
                {audit.data.status.length === 0 ? <div className="muted" style={{ fontSize: 12.5 }}>暂无状态变更记录。</div> : (
                  <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 14px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {audit.data.status.map((s) => {
                      const from = s.from_status ? deliveryStatus(s.from_status).label : '创建';
                      const to = deliveryStatus(s.to_status).label;
                      return (
                        <li key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5 }}>
                          <Pill tone={deliveryStatus(s.to_status).tone} label={to} />
                          <span className="muted">{from} → {to}</span>
                          {s.reason ? <span>· {s.reason}</span> : null}
                          <span style={{ marginLeft: 'auto', fontSize: 11 }}>
                            {s.changed_by_name || '系统'} · {new Date(s.created_at).toLocaleString('zh-CN')}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                )}
                <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>操作留痕（含改前 / 改后）</div>
                {audit.data.audit.length === 0 ? <div className="muted" style={{ fontSize: 12.5 }}>暂无操作日志。历史导入交货单可能仅有导入留痕。</div> : (
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {audit.data.audit.map((a) => (
                      <li key={a.id} style={{ fontSize: 12.5, borderBottom: '1px solid var(--line-soft)', paddingBottom: 6 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span className="num strong">{a.action}</span>
                          <span style={{ marginLeft: 'auto', fontSize: 11 }}>
                            {a.operator_name || '内部服务'} · {new Date(a.created_at).toLocaleString('zh-CN')}
                          </span>
                        </div>
                        {a.payload ? <div className="muted" style={{ fontSize: 11.5, marginTop: 2, wordBreak: 'break-all' }}>{JSON.stringify(a.payload)}</div> : null}
                      </li>
                    ))}
                  </ul>
                )}
              </>
            ) : null}
          </div>
        </div>
      ) : null}

      {confirm ? (
        <ConfirmDialog
          open
          title={`${confirm.label} · ${rec.number}`}
          message={`确认将交货单「${rec.number}」变更为「${deliveryStatus(confirm.to).label}」？`}
          detail={confirm.hint || '该操作会写入操作人、时间与改前/改后状态，并由服务端强制校验流转合法性与角色权限。'}
          confirmLabel={confirm.label}
          tone={confirm.tone === 'bad' ? 'danger' : 'primary'}
          requireReason={confirm.to === 'cancelled'}
          reasonPlaceholder="例如：客户拒收、发货计划变更…"
          busy={busy}
          onConfirm={(reason) => void doTransition(confirm, reason)}
          onCancel={() => setConfirm(null)}
        />
      ) : null}
    </div>
  );
}
